import { useState, useEffect } from 'react'
import { X, Mail, Calendar, Shield, CheckCircle, XCircle, Clock, Ban, User as UserIcon, CreditCard, FlaskConical } from 'lucide-react'
import usersService, { type User } from '@/services/usersService'
import plansService, { Plan } from '@/services/plansService'
import { UserUsageSection } from './users/UserUsageSection'
import { toast } from 'sonner'

// Rótulos das opções que o cadastro grava como código (id) no banco
const PROFILE_TYPE_LABELS: Record<string, string> = {
  producer: 'Produtor(a) Rural',
  technician: 'Técnico(a) / Consultor(a)',
  researcher: 'Pesquisador(a)',
  student: 'Estudante',
  institution: 'Representante de Instituição',
  other: 'Outro',
}

const HOW_KNEW_LABELS: Record<string, string> = {
  gaas: 'GAAS',
  indication: 'Indicação',
  event: 'Evento',
  social_media: 'Redes sociais',
  website: 'Site',
  other: 'Outro',
}

const AI_FAMILIARITY_LABELS: Record<string, string> = {
  never: 'Nunca usei',
  occasionally: 'Uso ocasionalmente',
  frequently: 'Uso com frequência',
}

const YES_NO_LABELS: Record<string, string> = {
  yes: 'Sim',
  no: 'Não',
  transition: 'Em fase de transição',
  dont_know: 'Ainda não sei',
}

const OBJECTIVE_LABELS: Record<string, string> = {
  doubts: 'Tirar dúvidas',
  recommendations: 'Receber recomendações',
  compare: 'Comparar com casos semelhantes',
  register: 'Registrar experiências',
  follow: 'Acompanhar a transição produtiva',
  learn: 'Aprender com ciência aplicada',
  research: 'Pesquisar',
  support: 'Apoiar produtores',
  develop: 'Desenvolver projetos',
  other: 'Outro',
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-2 text-sm py-1">
      <span className="text-gray-500 shrink-0">{label}:</span>
      <span className="text-gray-900 break-words">{value}</span>
    </div>
  )
}

interface UserDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  user: User | null
  onUserUpdated?: () => void
}

export function UserDetailsModal({ isOpen, onClose, user: userFromList, onUserUpdated }: UserDetailsModalProps) {
  const [plans, setPlans] = useState<Plan[]>([])
  const [changingPlan, setChangingPlan] = useState(false)
  const [selectedPlanId, setSelectedPlanId] = useState('')
  const [savingPlan, setSavingPlan] = useState(false)
  const [details, setDetails] = useState<User | null>(null)

  const userId = userFromList?.id

  useEffect(() => {
    if (isOpen) {
      plansService.listPlans().then(setPlans).catch(() => { })
      setChangingPlan(false)
    } else {
      setDetails(null)
    }
  }, [isOpen])

  // A listagem só traz campos básicos; os dados de perfil vêm do GET /users/:id
  useEffect(() => {
    if (!isOpen || !userId) return
    let active = true
    usersService
      .getUserById(userId)
      .then((full) => { if (active) setDetails(full) })
      .catch(() => { })
    return () => { active = false }
  }, [isOpen, userId])

  if (!isOpen || !userFromList) return null

  const user = details ?? userFromList

  const handleChangePlan = async () => {
    if (!selectedPlanId) return
    try {
      setSavingPlan(true)
      await plansService.assignPlanToUser(user.id, selectedPlanId)
      toast.success('Plano alterado com sucesso')
      setChangingPlan(false)
      // Recarrega os detalhes locais — `details` tem precedência sobre a prop,
      // então sem isso o plano exibido continuaria o antigo.
      setDetails(await usersService.getUserById(user.id))
      onUserUpdated?.()
    } catch (error) {
      toast.error('Erro ao alterar plano')
    } finally {
      setSavingPlan(false)
    }
  }

  const getRoleInfo = (role: string) => {
    const roleMap = {
      god: { label: 'God', color: 'text-purple-600', bg: 'bg-purple-50' },
      admin: { label: 'Admin', color: 'text-red-600', bg: 'bg-red-50' },
      creator: { label: 'Creator', color: 'text-blue-600', bg: 'bg-blue-50' },
      partner: { label: 'Partner', color: 'text-green-600', bg: 'bg-green-50' },
      user: { label: 'User', color: 'text-gray-600', bg: 'bg-gray-50' },
    }
    return roleMap[role as keyof typeof roleMap] || roleMap.user
  }

  const getStatusInfo = (status: string) => {
    const statusMap = {
      active: { label: 'Ativo', color: 'text-green-600', bg: 'bg-green-50', icon: CheckCircle },
      inactive: { label: 'Inativo', color: 'text-gray-600', bg: 'bg-gray-50', icon: Clock },
      suspended: { label: 'Suspenso', color: 'text-red-600', bg: 'bg-red-50', icon: Ban },
    }
    return statusMap[status as keyof typeof statusMap] || statusMap.inactive
  }

  const roleInfo = getRoleInfo(user.role)
  const statusInfo = getStatusInfo(user.status)
  const StatusIcon = statusInfo.icon

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold">Detalhes do Usuário</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Avatar e Nome */}
          <div className="flex items-center gap-4">
            {user.avatar_url ? (
              <img
                src={user.avatar_url}
                alt={user.name}
                className="h-20 w-20 rounded-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&size=80&background=random`
                }}
              />
            ) : (
              <div className="h-20 w-20 rounded-full bg-gray-200 flex items-center justify-center">
                <UserIcon className="h-10 w-10 text-gray-400" />
              </div>
            )}
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-2xl font-bold text-gray-900">{user.name}</h3>
                {user.is_test_user && (
                  <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <FlaskConical className="h-3 w-3" />
                    Teste
                  </span>
                )}
              </div>
              <p className="text-gray-500">{user.email}</p>
            </div>
          </div>

          {/* Status e Role */}
          <div className="grid grid-cols-2 gap-3">
            <div className={`${statusInfo.bg} ${statusInfo.color} px-4 py-3 rounded-lg flex items-center gap-2`}>
              <StatusIcon className="h-5 w-5" />
              <div>
                <p className="text-xs font-medium opacity-75">Status</p>
                <p className="font-semibold">{statusInfo.label}</p>
              </div>
            </div>
            <div className={`${roleInfo.bg} ${roleInfo.color} px-4 py-3 rounded-lg flex items-center gap-2`}>
              <Shield className="h-5 w-5" />
              <div>
                <p className="text-xs font-medium opacity-75">Role</p>
                <p className="font-semibold">{roleInfo.label}</p>
              </div>
            </div>
          </div>

          {/* Informações de Contato */}
          <div>
            <h4 className="text-lg font-semibold mb-3 text-gray-900">Informações de Contato</h4>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-gray-400" />
                <a href={`mailto:${user.email}`} className="text-blue-600 hover:underline">
                  {user.email}
                </a>
                {user.email_verified && (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Verificado
                  </span>
                )}
                {!user.email_verified && (
                  <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <XCircle className="h-3 w-3" />
                    Não verificado
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Datas */}
          <div>
            <h4 className="text-lg font-semibold mb-3 text-gray-900">Informações do Sistema</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span className="text-gray-500">Cadastrado em:</span>
                <span className="text-gray-900">
                  {new Date(user.created_at).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
              {user.updated_at && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-500">Última atualização:</span>
                  <span className="text-gray-900">
                    {new Date(user.updated_at).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              )}
              {user.last_login_at && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-500">Último login:</span>
                  <span className="text-gray-900">
                    {new Date(user.last_login_at).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Plano */}
          <div>
            <h4 className="text-lg font-semibold mb-3 text-gray-900">Plano de Assinatura</h4>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 flex-1">
                <CreditCard className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-700 font-medium">
                  {user.plan_name || 'Sem plano'}
                </span>
                {user.plan_slug && (
                  <span className="text-xs text-gray-500">({user.plan_slug})</span>
                )}
              </div>
              {!changingPlan ? (
                <button
                  onClick={() => {
                    setChangingPlan(true)
                    setSelectedPlanId(user.plan_id || '')
                  }}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Alterar
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <select
                    value={selectedPlanId}
                    onChange={(e) => setSelectedPlanId(e.target.value)}
                    className="text-sm border border-gray-300 rounded-md px-2 py-1"
                  >
                    <option value="">Selecione...</option>
                    {plans.filter(p => p.isActive).map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                  <button
                    onClick={handleChangePlan}
                    disabled={!selectedPlanId || savingPlan}
                    className="text-sm bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {savingPlan ? '...' : 'Salvar'}
                  </button>
                  <button
                    onClick={() => setChangingPlan(false)}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Cancelar
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Dados de Perfil (preenchidos no cadastro ou pelo admin) */}
          {details && (
            <div>
              <h4 className="text-lg font-semibold mb-3 text-gray-900">Dados de Perfil</h4>
              {(() => {
                const rows: { label: string; value: React.ReactNode }[] = []
                const push = (label: string, value: unknown, map?: Record<string, string>) => {
                  if (value === null || value === undefined || value === '') return
                  if (Array.isArray(value)) {
                    if (value.length === 0) return
                    rows.push({ label, value: value.join(', ') })
                    return
                  }
                  const raw = String(value)
                  rows.push({ label, value: map?.[raw] ?? raw })
                }

                push('Tipo de perfil', user.profile_type, PROFILE_TYPE_LABELS)
                push('Username', user.username)
                push('CPF', user.cpf)
                push('Telefone', user.phone)
                push('Cidade', user.city)
                push('UF', user.state)
                push('Profissão', user.profession)
                push('Como conheceu', user.how_knew, HOW_KNEW_LABELS)
                push('Familiaridade com IA', user.ai_familiarity, AI_FAMILIARITY_LABELS)

                push('Área (ha)', user.property_size_ha)
                push('Bioma', user.biome)
                push('Tipo de solo', user.soil_type)
                push('Cidade da propriedade', user.property_city)
                push('UF da propriedade', user.property_state)
                push('Sistema de produção', user.production_system)
                push('Associado ao GAAS', user.gaas_associated, YES_NO_LABELS)
                push('Objetivo principal', user.main_objective, OBJECTIVE_LABELS)
                push('Atividades', user.activities)
                push('Práticas', user.practices)
                push('Desafios', user.challenges)

                push('Formação', user.professional_formation)
                push('Área de atuação', user.work_area)
                push('Região de atuação', user.work_region)
                push('Tipo de vínculo', user.work_type)
                push('Agricultura regenerativa', user.regenerative_experience, YES_NO_LABELS)
                push('Tempo de atuação', user.experience_years)

                push('Instituição', user.institution)
                push('Tipo de instituição', user.work_field)
                push('Interesse principal', user.main_interest, OBJECTIVE_LABELS)
                push('Temas de interesse', user.preferred_topics)

                push('Bio', user.bio)
                push('Observações', user.observations)

                rows.push({
                  label: 'Termos aceitos',
                  value: user.terms_accepted ? 'Sim' : 'Não',
                })
                rows.push({
                  label: 'Compartilhamento de dados',
                  value: user.data_sharing_consent ? 'Sim' : 'Não',
                })

                if (rows.length === 2) {
                  return <p className="text-sm text-gray-500">Nenhum dado de perfil preenchido.</p>
                }

                return (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                    {rows.map((row) => (
                      <DetailRow key={row.label} label={row.label} value={row.value} />
                    ))}
                  </div>
                )
              })()}
            </div>
          )}

          {/* Consumo de IA */}
          <UserUsageSection userId={user.id} />

          {/* ID do Usuário */}
          <div>
            <h4 className="text-lg font-semibold mb-2 text-gray-900">ID do Usuário</h4>
            <div className="bg-gray-50 p-3 rounded-lg">
              <code className="text-xs text-gray-700 font-mono break-all">{user.id}</code>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  )
}
